import { HubConnectionState } from '@microsoft/signalr';
import { noop } from 'lodash-es';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCatchUpParams } from 'src/components/Listings/InventoryEventPage/InventoryEventPage.hooks';
import { InventoryEventTabs } from 'src/components/Listings/InventoryEventPage/InventoryEventTabs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  EventIdQueryParam,
  EventSessionIdQueryParam,
  EventSessionRoleQueryParam,
} from 'src/utils/constants/constants';
import { newGuidId } from 'src/utils/idUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingClient,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useClientLogContext } from '../ClientLogContext';
import { POSSignalRHub } from '../SignalRConnection';

export enum EventSessionRole {
  Main = 'main',
  SidePanel = 'sidePanel',
  Portfolio = 'portfolio',
  CompanionEvent = 'CompanionEvent',
}

export type IEventHubContext = {
  hubConnection?: POSSignalRHub;
  onStartSession: () => void;
  onCancelSession: () => void;
  onSelectEvent: (eventVirtualId?: string, forceStart?: boolean) => void;
  onSelectListings: (listingIds: number[]) => void;
  onSectionsSelected: (sectionIds: number[]) => void;
  onUpdateListing: (listingId: number) => void;
  onUpdateInventoryTab: (tab: InventoryEventTabs) => void;
  sessionRole: EventSessionRole | undefined;
  selectedEventVirtualId: string | undefined;
  selectedListingIds: number[] | undefined;
  selectedVenueMapSections: number[];
  inventoryTab: InventoryEventTabs | undefined;
  enabled: boolean;
  isOnSidePanel: boolean;
  isOnCompanionEvent: boolean;
};

export const EventHubContext = createContext<IEventHubContext>({
  hubConnection: undefined,
  onStartSession: noop,
  onCancelSession: noop,
  onSelectEvent: noop,
  onSelectListings: noop,
  onSectionsSelected: noop,
  onUpdateListing: noop,
  onUpdateInventoryTab: noop,
  sessionRole: undefined,
  selectedEventVirtualId: undefined,
  selectedListingIds: undefined,
  selectedVenueMapSections: [],
  inventoryTab: undefined,
  enabled: false,
  isOnSidePanel: false,
  isOnCompanionEvent: false,
});

export const useEventHubContext = () => useContext(EventHubContext);

export function EventHubContextProvider({ children }: { children: ReactNode }) {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasEventMultiScreenFeature = useUserHasFeature(
    Feature.EventMultiScreen
  );
  const hasCompanionModeInInsightViewFeature = useUserHasFeature(
    Feature.CompanionModeInInsightView
  );

  const { updateItemInEvent } = useCatalogDataContext();
  const [hubConnection, setHubConnection] = useState<POSSignalRHub>();
  const { logEvent } = useClientLogContext();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { isCatchupMode } = useCatchUpParams();

  const sessionId = useMemo(
    () => searchParams.get(EventSessionIdQueryParam) ?? undefined,
    [searchParams]
  );
  const sessionRole = useMemo(
    () => searchParams.get(EventSessionRoleQueryParam) ?? undefined,
    [searchParams]
  );

  const [selectedEventVirtualId, setSelectedEventVirtualId] =
    useState<string>();
  const [selectedListingIds, setSelectedListingIds] = useState<number[]>();
  const [inventoryTab, setInventoryTab] = useState<InventoryEventTabs>();
  const [selectedVenueMapSections, setSelectedVenueMapSections] = useState<
    number[]
  >([]);

  const reset = useCallback(() => {
    setSelectedEventVirtualId(undefined);
    setSelectedListingIds(undefined);
    setInventoryTab(undefined);
    setSelectedVenueMapSections([]);
  }, []);

  useEffect(() => {
    // Only attempt to connect to signal R when there is a login
    if (
      hasEventMultiScreenFeature &&
      activeAccountWebClientConfig.activeAccountId
    ) {
      if (
        !hubConnection ||
        hubConnection.state === HubConnectionState.Disconnected ||
        hubConnection.state === HubConnectionState.Disconnecting
      ) {
        const newHubConnection = new POSSignalRHub(
          '/hubs/event',
          activeAccountWebClientConfig,
          logEvent
        );

        newHubConnection
          .start()
          .then(() => {
            setHubConnection(newHubConnection);
            console.debug(
              `Event connection ready with id: ${newHubConnection.connectionId}`
            );
          })
          .catch((err) => {
            setHubConnection(undefined);
            console.warn('Event could not be started at this time: ' + err);
          });

        newHubConnection.onclose((err) => {
          setHubConnection(undefined);
          console.debug('Event connection closed', err);
        });
      }
    }

    return () => {
      if (
        hubConnection &&
        !(
          hubConnection.state === HubConnectionState.Disconnected ||
          hubConnection.state === HubConnectionState.Disconnecting
        )
      ) {
        console.debug(
          `Stopping ListingNotification connection (state: ${hubConnection.state}) with id: ${hubConnection.connectionId}`
        );
        hubConnection.stop();
        setHubConnection(undefined);
        reset();
      }
    };
  }, [
    activeAccountWebClientConfig,
    hasEventMultiScreenFeature,
    hubConnection,
    logEvent,
    reset,
  ]);

  const onStartSession = useCallback(() => {
    if (sessionRole || !hasEventMultiScreenFeature) {
      return;
    }
    const sessionId = newGuidId();

    searchParams.set(EventSessionIdQueryParam, sessionId);
    searchParams.set(EventSessionRoleQueryParam, EventSessionRole.Main);

    navigate(`/inventory/event?${searchParams.toString()}`, {
      replace: true,
    });

    searchParams.set(EventSessionRoleQueryParam, EventSessionRole.SidePanel);

    window.open(`/inventory/event?${searchParams.toString()}`, '_blank');
  }, [hasEventMultiScreenFeature, navigate, searchParams, sessionRole]);

  const onCancelSession = useCallback(() => {
    if (hasEventMultiScreenFeature && sessionId) {
      hubConnection?.invoke('StopSession', sessionId);
    }
  }, [hasEventMultiScreenFeature, hubConnection, sessionId]);

  const onSelectEvent = useCallback(
    (eventVirtualId?: string, forceStart = false) => {
      if (eventVirtualId === selectedEventVirtualId) {
        return;
      }
      // Only allow event selection when force start or on Portfolio role
      if (!(forceStart || sessionRole === EventSessionRole.Portfolio)) {
        return;
      }
      if (
        hasEventMultiScreenFeature &&
        hasCompanionModeInInsightViewFeature &&
        eventVirtualId
      ) {
        const sanitizedSessionId = sessionId ?? newGuidId();
        // Start the componanion session
        if (sessionRole !== EventSessionRole.Portfolio) {
          searchParams.set(EventSessionIdQueryParam, sanitizedSessionId);
          searchParams.set(
            EventSessionRoleQueryParam,
            EventSessionRole.CompanionEvent
          );
          searchParams.set(EventIdQueryParam, eventVirtualId);
          // Open the companion page
          window.open(`/inventory/event?${searchParams.toString()}`, '_blank');

          searchParams.delete(EventIdQueryParam);
          searchParams.set(
            EventSessionRoleQueryParam,
            EventSessionRole.Portfolio
          );
          // Add the session data to current URL.
          navigate(`/inventory?${searchParams.toString()}`, {
            replace: true,
          });
        }
        hubConnection?.invoke(
          'SelectEvent',
          sanitizedSessionId,
          eventVirtualId
        );
        setSelectedEventVirtualId(eventVirtualId);
      }
    },
    [
      selectedEventVirtualId,
      hasEventMultiScreenFeature,
      hasCompanionModeInInsightViewFeature,
      sessionId,
      sessionRole,
      hubConnection,
      searchParams,
      navigate,
    ]
  );

  const onSelectListings = useCallback(
    (listingIds: number[]) => {
      if (hasEventMultiScreenFeature && sessionId) {
        hubConnection?.invoke(
          'SelectListings',
          sessionId,
          JSON.stringify(listingIds)
        );
      }
    },
    [hasEventMultiScreenFeature, sessionId, hubConnection]
  );

  const onUpdateListing = useCallback(
    (listingId: number) => {
      if (hasEventMultiScreenFeature && sessionId && sessionRole && listingId) {
        hubConnection?.invoke(
          'UpdateListing',
          sessionId,
          listingId,
          sessionRole
        );
      }
    },
    [hasEventMultiScreenFeature, hubConnection, sessionId, sessionRole]
  );

  // This is to broadcast the venue map section selection from side panel to main
  const onSectionsSelected = useCallback(
    (selectedSections: number[]) => {
      if (
        hasEventMultiScreenFeature &&
        sessionId &&
        sessionRole === EventSessionRole.SidePanel
      ) {
        hubConnection?.invoke(
          'UpdateSelectedSections',
          sessionId,
          JSON.stringify(selectedSections),
          sessionRole
        );
      }
    },
    [hasEventMultiScreenFeature, hubConnection, sessionId, sessionRole]
  );

  // This is to broadcast the event inventory tab update from main to side panel
  const onUpdateInventoryTab = useCallback(
    (inventoryTab: InventoryEventTabs) => {
      if (
        hasEventMultiScreenFeature &&
        sessionId &&
        sessionRole === EventSessionRole.Main &&
        inventoryTab
      ) {
        hubConnection?.invoke(
          'UpdateInventoryTab',
          sessionId,
          inventoryTab,
          sessionRole
        );
      }
    },
    [hasEventMultiScreenFeature, hubConnection, sessionId, sessionRole]
  );

  useEffect(() => {
    const onEventSelected = (
      serverSessionId: string,
      eventVirtualId: string
    ) => {
      if (
        sessionId === serverSessionId &&
        sessionRole === EventSessionRole.CompanionEvent &&
        hasCompanionModeInInsightViewFeature &&
        eventVirtualId
      ) {
        setSelectedEventVirtualId(eventVirtualId);
      }
    };
    const onListingsSelected = (
      serverSessionId: string,
      listingIdsJson: string
    ) => {
      if (
        sessionId === serverSessionId &&
        sessionRole === EventSessionRole.SidePanel &&
        listingIdsJson
      ) {
        setSelectedListingIds(JSON.parse(listingIdsJson) as number[]);
      }
    };
    const onStopSession = (serverSessionId: string) => {
      if (sessionId !== serverSessionId) {
        return;
      }
      if (
        sessionRole === EventSessionRole.SidePanel ||
        sessionRole === EventSessionRole.Main
      ) {
        searchParams.delete(EventSessionIdQueryParam);
        searchParams.delete(EventSessionRoleQueryParam);

        navigate(`/inventory/event?${searchParams.toString()}`, {
          replace: true,
        });
      }

      if (
        (sessionRole === EventSessionRole.Portfolio ||
          sessionRole === EventSessionRole.CompanionEvent) &&
        hasCompanionModeInInsightViewFeature
      ) {
        searchParams.delete(EventSessionIdQueryParam);
        searchParams.delete(EventSessionRoleQueryParam);
        const baseUrl =
          sessionRole === EventSessionRole.Portfolio
            ? '/inventory'
            : '/inventory/event';

        navigate(`${baseUrl}?${searchParams.toString()}`, {
          replace: true,
        });
      }

      reset();
    };
    const onListingUpdated = async (
      serverSessionId: string,
      listingId: number,
      fromRole: EventSessionRole
    ) => {
      if (
        activeAccountWebClientConfig.activeAccountId &&
        hasEventMultiScreenFeature &&
        sessionId === serverSessionId &&
        fromRole !== sessionRole
      ) {
        // Refersh listing (whole - since we don't know which part of the listing was updated)
        const updated = await new ListingClient({
          activeAccountId: activeAccountWebClientConfig.activeAccountId,
        }).getListingByListingId(listingId, undefined, undefined);
        if (updated) {
          updateItemInEvent(updated, ActionOutboxEntityType.Listing);
        }
      }
    };
    const onInventoryTabUpdated = (
      serverSessionId: string,
      tab: InventoryEventTabs,
      _: EventSessionRole
    ) => {
      if (
        sessionId === serverSessionId &&
        sessionRole === EventSessionRole.SidePanel
      ) {
        setInventoryTab(tab);
      }
    };
    const onVenueMapSectionsUpdated = (
      serverSessionId: string,
      selectedSectionsJson: string,
      _: EventSessionRole
    ) => {
      if (
        sessionId === serverSessionId &&
        sessionRole === EventSessionRole.Main
      ) {
        setSelectedVenueMapSections(
          JSON.parse(selectedSectionsJson) as number[]
        );
      }
    };
    hubConnection?.on('OnEventSelected', onEventSelected);
    hubConnection?.on('OnListingsSelected', onListingsSelected);
    hubConnection?.on('OnListingUpdated', onListingUpdated);
    hubConnection?.on('OnInventoryTabUpdated', onInventoryTabUpdated);
    hubConnection?.on('OnVenueMapSectionsUpdated', onVenueMapSectionsUpdated);
    hubConnection?.on('OnStopSession', onStopSession);

    return () => {
      hubConnection?.off('OnEventSelected', onEventSelected);
      hubConnection?.off('OnListingSelected', onListingsSelected);
      hubConnection?.off('OnListingUpdated', onListingUpdated);
      hubConnection?.off('OnInventoryTabUpdated', onInventoryTabUpdated);
      hubConnection?.off(
        'OnVenueMapSectionsUpdated',
        onVenueMapSectionsUpdated
      );
      hubConnection?.off('OnStopSession', onStopSession);
    };
  }, [
    sessionId,
    hubConnection,
    sessionRole,
    onStartSession,
    onCancelSession,
    searchParams,
    navigate,
    reset,
    hasEventMultiScreenFeature,
    activeAccountWebClientConfig.activeAccountId,
    updateItemInEvent,
    setInventoryTab,
    hasCompanionModeInInsightViewFeature,
  ]);

  const enabled = useMemo(() => {
    // Disable when feature is off
    if (!hasEventMultiScreenFeature) {
      return false;
    }

    if (!hubConnection) {
      return false;
    }

    // disable for catchup mode
    if (isCatchupMode) {
      return false;
    }

    return true;
  }, [hasEventMultiScreenFeature, hubConnection, isCatchupMode]);

  const isOnSidePanel = useMemo(() => {
    return enabled && sessionRole === EventSessionRole.SidePanel;
  }, [enabled, sessionRole]);
  const isOnCompanionEvent = useMemo(() => {
    return (
      enabled &&
      hasCompanionModeInInsightViewFeature &&
      sessionRole === EventSessionRole.CompanionEvent
    );
  }, [enabled, hasCompanionModeInInsightViewFeature, sessionRole]);

  return (
    <EventHubContext.Provider
      value={{
        enabled,
        hubConnection,
        onStartSession,
        onCancelSession,
        onSelectEvent,
        onSelectListings,
        onSectionsSelected,
        onUpdateListing,
        onUpdateInventoryTab,
        sessionRole: sessionRole
          ? (sessionRole as EventSessionRole)
          : undefined,
        selectedEventVirtualId,
        selectedListingIds,
        selectedVenueMapSections,
        inventoryTab,
        isOnSidePanel,
        isOnCompanionEvent,
      }}
    >
      {children}
    </EventHubContext.Provider>
  );
}
